
const COLOR_SCHEMES = {
	// Blue: Completed OR Past sessions
	past: {
		textColor: '#0369A1',
		iconBgColor: '#0EA5E9',
		bgColor: '#E7F6FD',
		borderColor: '#0EA5E9',
	},
	// Green: Today's sessions (not completed)
	today: {
		textColor: '#067647',
		iconBgColor: '#17B26A',
		bgColor: '#ECFDF3',
		borderColor: '#17B26A',
	},
	// Orange: Future sessions
	future: {
		textColor: '#B54708',
		iconBgColor: '#F79009',
		bgColor: '#FFFAEB',
		borderColor: '#F79009',
	},
	// Red: Cancelled sessions
	canceled: {
		textColor: '#B42318',
		iconBgColor: '#F04438',
		bgColor: '#FEF3F2',
		borderColor: '#F04438',
	},
};

/**
 * Get date status and colors based on date, time and canceled flag
 * @param {Date|string} dateInput - Date object or string in YYYYMMDD format
 * @param {string} timeStr - Time string in HH:mm:ss format (optional)
 * @param {boolean} canceled - Whether session is canceled
 * @returns {Object} { key, label, textColor, iconBgColor, bgColor, borderColor }
 */
export function getDateStatus(dateInput, timeStr = '', canceled = false) {
	// Check canceled first
	if (canceled) {
		return {
			key: 'canceled',
			label: wpcore.direct_t({ en: 'Cancelled', fr: 'Annul\u00e9e' }),
			...COLOR_SCHEMES.canceled,
		};
	}

	let sessionDate;

	// Handle Date object or string input
	if (dateInput instanceof Date) {
		sessionDate = new Date(dateInput);
	} else if (typeof dateInput === 'string' && dateInput.length === 8) {
		// Ymd format: 20260323
		const year = parseInt(dateInput.substring(0, 4), 10);
		const month = parseInt(dateInput.substring(4, 6), 10) - 1;
		const day = parseInt(dateInput.substring(6, 8), 10);
		sessionDate = new Date(year, month, day);
	} else if (typeof dateInput === 'string' && dateInput.includes('/')) {
		// d/m/Y format: 23/03/2026
		const parts = dateInput.split('/');
		if (parts.length === 3) {
			sessionDate = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
		}
	} else if (typeof dateInput === 'string' && dateInput.includes('-')) {
		// Y-m-d format: 2026-03-23
		sessionDate = new Date(dateInput + 'T00:00:00');
	}

	if (!sessionDate || isNaN(sessionDate.getTime())) {
		return {
			key: 'upcoming',
			label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
			...COLOR_SCHEMES.future,
		};
	}

	const today = new Date();
	const now = new Date();

	sessionDate.setHours(0, 0, 0, 0);
	today.setHours(0, 0, 0, 0);

	// Past date
	if (sessionDate < today) {
		return {
			key: 'past',
			label: wpcore.direct_t({ en: 'Completed', fr: 'Termin\u00e9e' }),
			...COLOR_SCHEMES.past,
		};
	}

	// Today - check time if provided
	if (sessionDate.getTime() === today.getTime()) {
		if (timeStr) {
			const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10) || 0);
			if (
				h < now.getHours() ||
				(h === now.getHours() && m < now.getMinutes())
			) {
				return {
					key: 'past',
					label: wpcore.direct_t({ en: 'Completed', fr: 'Termin\u00e9e' }),
					...COLOR_SCHEMES.past,
				};
			}
		}
		// Today but not past time - use today colors
		return {
			key: 'upcoming',
			label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
			...COLOR_SCHEMES.today,
		};
	}

	// Future
	return {
		key: 'upcoming',
		label: wpcore.direct_t({ en: 'Upcoming', fr: '\u00c0 venir' }),
		...COLOR_SCHEMES.future,
	};
}

export function updateSessionButtons(sessionData) {
	const elements = {
		sendNoteBtn: document.getElementById('session-popup-send-note'),
		rescheduleBtn: document.getElementById('session-popup-reschedule'),
		joinBtn: document.getElementById('session-popup-join'),
		cancelBtn: document.getElementById('session-popup-cancel'),
		notesWrapper: document.getElementById('coach_notes_wrapper'),
		notesTextarea: document.getElementById('coach_notes'),
		todayLabel: document.getElementById('today-label'),
		linkInput: document.getElementById('link_input'),
	};

	if (!sessionData) return;

	// Hide all by default (except notesWrapper and notesTextarea which are inside wrapper)
	Object.values(elements).forEach((el) => {
		if (
			el &&
			el.style &&
			el !== elements.notesWrapper &&
			el !== elements.notesTextarea
		) {
			el.style.display = 'none';
		}
	});

	// Ensure notes wrapper is hidden (uses hidden class)
	if (elements.notesWrapper) elements.notesWrapper.classList.add('hidden');

	// Use server-calculated flags (wp_timezone consistency)
	const isPast = sessionData.is_past || false;
	const isUpcoming = sessionData.is_upcoming || false;
	const isToday = sessionData.is_today || false;
	const canJoin = sessionData.can_join || false;
	const is24hBefore = sessionData.is_24h_before || false;
	const isCanceled = sessionData.canceled || false;

	// Show today label if applicable
	if (isToday && elements.todayLabel) {
		elements.todayLabel.style.display = 'inline-flex';
	}

	// Show buttons based on status
	if (isPast) {
		// Past session: show notes
		if (elements.notesWrapper) {
			elements.notesWrapper.classList.remove('hidden');
			// Only show send note button if notes is empty
			if (elements.sendNoteBtn && !sessionData.notes) {
				elements.sendNoteBtn.style.display = 'block';
			}
		}
	} else if (isUpcoming) {
		// Upcoming session
		// Show link input
		if (elements.linkInput) elements.linkInput.style.display = 'block';

		// Join button: always visible, disabled when !canJoin
		if (elements.joinBtn) {
			elements.joinBtn.style.display = 'flex';
			if (canJoin) {
				elements.joinBtn.disabled = false;
				elements.joinBtn.classList.remove('opacity-50', 'cursor-not-allowed');
				elements.joinBtn.classList.add('cursor-pointer');
			} else {
				elements.joinBtn.disabled = true;
				elements.joinBtn.classList.add('opacity-50', 'cursor-not-allowed');
				elements.joinBtn.classList.remove('cursor-pointer');
			}
		}

		// Reschedule: show when 24h before
		if (is24hBefore && elements.rescheduleBtn) {
			elements.rescheduleBtn.style.display = 'block';
			elements.rescheduleBtn.href = `/coach/visio-management/re-schedule/?session_id=${sessionData.id}`;
		}

		// Cancel: show when 24h before AND not canceled
		if (is24hBefore && !isCanceled && elements.cancelBtn) {
			elements.cancelBtn.style.display = 'block';
		}
	}
}

export function parseSessionTimestamp(dateStr, timeStr) {
	if (!dateStr || dateStr.length !== 8) return 0;

	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);

	let hours = 0;
	let minutes = 0;

	if (timeStr) {
		const timeParts = timeStr.split(':');
		hours = parseInt(timeParts[0], 10) || 0;
		minutes = parseInt(timeParts[1], 10) || 0;
	}

	const date = new Date(year, month, day, hours, minutes, 0, 0);
	return date.getTime();
}

/**
 * Check if date string is today
 * @param {string} dateStr - Date string in YYYYMMDD format
 * @returns {boolean}
 */
export function checkIfToday(dateStr) {
	if (!dateStr || dateStr.length !== 8) return false;

	const year = parseInt(dateStr.substring(0, 4), 10);
	const month = parseInt(dateStr.substring(4, 6), 10) - 1;
	const day = parseInt(dateStr.substring(6, 8), 10);

	const sessionDate = new Date(year, month, day);
	const today = new Date();

	sessionDate.setHours(0, 0, 0, 0);
	today.setHours(0, 0, 0, 0);

	return sessionDate.getTime() === today.getTime();
}
